import { useState, useEffect, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Send, Mail, Paperclip, AlertTriangle } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { Modal } from '../ui/Modal.jsx';
import { Button } from '../ui/Button.jsx';
import { Textarea } from '../ui/Form.jsx';

// =============================================================================
// EmailPreviewModal — universal preview-before-send component
// =============================================================================
//
// LOCKED RULE: every dispatcher-clicked email send across dispatch goes
// through this modal first. Auto-fired emails skip it.
//
// Accepts EITHER:
//   - A single email (props: previewParams, sendEndpoint, sendBody, title…)
//   - An array of emails via `emails` prop — renders tabs at the top, one
//     preview per tab. Single Send button at the bottom fires ALL of them.
//
// `emails` shape: [{
//     id:            unique key (e.g. 'customer', 'driver')
//     label:         tab label (e.g. 'Customer', 'New chauffeur')
//     previewParams: body of POST /email-previews
//     sendEndpoint:  e.g. `/bookings/${id}/send-details`
//     sendBody:      (recipients) => requestBody
// }]
// =============================================================================

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Email kinds whose subject the dispatcher may edit before sending.
// Backend must honour subject_override for each kind listed here.
const SUBJECT_EDITABLE_KINDS = new Set(['customer_chauffeur_allocated', 'chauffeur_allocation']);

function parseRecipientTokens(input) {
    return (input || '')
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}

export default function EmailPreviewModal({
    // Multi-email mode
    emails,
    // Single-email mode (legacy / SendEmailModal path)
    previewParams,
    sendEndpoint,
    sendBody,
    title = 'Send email',
    description,
    onClose,
    onSent,
}) {
    // Normalise single-email props into the multi-email shape so everything
    // downstream only deals with one code path.
    const normalisedEmails = useMemo(() => {
        if (emails && emails.length > 0) return emails;
        if (previewParams && sendEndpoint) {
            return [{
                id: 'single',
                label: title,
                previewParams,
                sendEndpoint,
                sendBody,
            }];
        }
        return [];
    }, [emails, previewParams, sendEndpoint, sendBody, title]);

    const [activeTab, setActiveTab] = useState(0);

    // Reset tab when emails change
    useEffect(() => {
        setActiveTab(0);
    }, [normalisedEmails.length]);

    const isMulti = normalisedEmails.length > 1;
    const modalTitle = isMulti
        ? `Send ${normalisedEmails.length} emails`
        : (normalisedEmails[0]?.label || title);
    const modalDescription = isMulti
        ? 'Review each email below. Recipients can be edited per tab.'
        : (description || 'Review the email below. Recipients can be edited; subject and body are locked.');

    return (
        <Modal
            open
            onClose={onClose}
            title={modalTitle}
            description={modalDescription}
            size="lg"
        >
            <EmailPreviewBody
                emails={normalisedEmails}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onClose={onClose}
                onSent={onSent}
            />
        </Modal>
    );
}

// =============================================================================
// Inner body — manages per-email state and the send-all flow
// =============================================================================
function EmailPreviewBody({ emails, activeTab, onTabChange, onClose, onSent }) {
    const api = useApi();
    const isMulti = emails.length > 1;

    // Per-email state, keyed by email id. Each email tracks its own preview
    // data, recipients, errors, and send status.
    const [emailStates, setEmailStates] = useState(() =>
        emails.reduce((acc, e) => {
            acc[e.id] = {
                preview: null,
                previewError: null,
                previewLoading: true,
                recipientsInput: '',
                                recipientsError: null,
                subjectInput: '',
                sent: false,
            };
            return acc;
        }, {})
    );

    // Fetch all previews in parallel on mount.
    useEffect(() => {
        let cancelled = false;
        emails.forEach((email) => {
            api.post('/email-previews', email.previewParams)
                .then((data) => {
                    if (cancelled) return;
                    setEmailStates((prev) => ({
                        ...prev,
                        [email.id]: {
                            ...prev[email.id],
                            preview: data,
                            previewLoading: false,
                                                        recipientsInput: (data.defaultRecipients || []).join(', '),
                            subjectInput: data.subject || '',
                        },
                    }));
                })
                .catch((err) => {
                    if (cancelled) return;
                    const msg = err?.response?.data?.error || err?.message || 'Failed to load preview';
                    setEmailStates((prev) => ({
                        ...prev,
                        [email.id]: {
                            ...prev[email.id],
                            previewError: msg,
                            previewLoading: false,
                        },
                    }));
                });
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function updateState(emailId, patch) {
        setEmailStates((prev) => ({
            ...prev,
            [emailId]: { ...prev[emailId], ...patch },
        }));
    }

    function handleRecipientsChange(emailId, value) {
        const tokens = parseRecipientTokens(value);
        const invalid = tokens.filter((t) => !EMAIL_RE.test(t));
        const error = tokens.length > 0 && invalid.length > 0
            ? `Invalid: ${invalid.join(', ')}`
            : null;
        updateState(emailId, { recipientsInput: value, recipientsError: error });
    }

    function getRecipients(emailId) {
        const tokens = parseRecipientTokens(emailStates[emailId]?.recipientsInput || '');
        const valid = tokens.filter((t) => EMAIL_RE.test(t));
        return Array.from(new Set(valid.map((r) => r.toLowerCase())));
    }

    // Send all emails. Each one fires independently; failures are toasted
    // but don't block the others.
    const sendMutation = useMutation({
        mutationFn: async () => {
            const results = await Promise.all(
                emails.map(async (email) => {
                    if (emailStates[email.id]?.sent) {
                        return { email, skipped: true };
                    }
                    const recipients = getRecipients(email.id);
                    if (recipients.length === 0) {
                        return { email, error: 'No recipients' };
                    }
                    try {
                                                let body = email.sendBody
                            ? email.sendBody(recipients)
                            : { recipients };
                        const st = emailStates[email.id];
                        const editedSubject = (st?.subjectInput || '').trim();
                        if (
                            SUBJECT_EDITABLE_KINDS.has(email.previewParams?.kind)
                            && editedSubject
                            && editedSubject !== st?.preview?.subject
                        ) {
                            body = { ...body, subject_override: editedSubject };
                        }
                        const response = await api.post(email.sendEndpoint, body);
                        updateState(email.id, { sent: true });
                        return { email, response, recipients };
                    } catch (err) {
                        const msg = err?.response?.data?.error || err?.message || 'Send failed';
                        return { email, error: msg };
                    }
                })
            );
            return results;
        },
        onSuccess: (results) => {
            const sent = results.filter((r) => r.response);
            const failed = results.filter((r) => r.error);

            if (sent.length > 0) {
                if (sent.length === 1) {
                    toast.success(`Sent to ${sent[0].recipients.length} recipient${sent[0].recipients.length !== 1 ? 's' : ''}`);
                } else {
                    toast.success(`${sent.length} emails sent`);
                }
            }
            for (const r of failed) {
                toast.error(`${r.email.label} — ${r.error}`);
            }

            if (failed.length === 0) {
                if (onSent) onSent(results);
                else onClose();
            }
        },
    });

    // Send button is enabled when ALL emails have valid recipients and no errors
    const allReady = emails.every((email) => {
        const state = emailStates[email.id];
        if (!state?.preview) return false;
        if (state.recipientsError) return false;
        return getRecipients(email.id).length > 0;
    });

    const active = emails[activeTab];
    const activeState = emailStates[active?.id];

    return (
        <>
            {/* Tabs — only render when there's more than one email */}
            {isMulti && (
                <div className="flex gap-1 border-b border-paper-border-soft -mx-4 sm:-mx-5 px-4 sm:px-5 mb-4 overflow-x-auto">
                    {emails.map((email, i) => {
                        const state = emailStates[email.id];
                        const isActive = i === activeTab;
                        return (
                            <button
                                key={email.id}
                                type="button"
                                onClick={() => onTabChange(i)}
                                className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                                    isActive
                                        ? 'border-ink text-ink'
                                        : 'border-transparent text-ink-subtle hover:text-ink'
                                }`}
                            >
                                {email.label}
                                {state?.sent && <span className="ml-1.5 text-green-600">✓</span>}
                                {state?.previewError && <span className="ml-1.5 text-red-600">!</span>}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Active email body */}
            {active && activeState && (
                <div className="space-y-4 mt-2">
                    {activeState.previewLoading && (
                        <div className="text-sm text-ink-subtle italic py-8 text-center">
                            Loading preview…
                        </div>
                    )}

                    {activeState.previewError && (
                        <div className="bg-red-50 border border-red-200 rounded px-3 py-2.5 text-sm text-red-800 flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <div>
                                <div className="font-medium">Couldn't load preview</div>
                                <div className="text-xs mt-0.5">{activeState.previewError}</div>
                            </div>
                        </div>
                    )}

                    {activeState.preview && (
                        <>
                            <div className="rounded-lg border border-paper-border-soft bg-canvas/30 p-3 space-y-2 text-sm">
                                <MetaRow label="From" value={activeState.preview.from} mono />
                                {activeState.preview.replyTo && activeState.preview.replyTo !== activeState.preview.from && (
                                    <MetaRow label="Reply to" value={activeState.preview.replyTo} mono />
                                )}
                                                                {SUBJECT_EDITABLE_KINDS.has(active.previewParams?.kind) ? (
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="text-xs uppercase tracking-wide text-ink-subtle font-medium w-16 flex-shrink-0">
                                            Subject
                                        </span>
                                        <input
                                            type="text"
                                            value={activeState.subjectInput}
                                            onChange={(e) => updateState(active.id, { subjectInput: e.target.value })}
                                            maxLength={200}
                                            disabled={sendMutation.isPending || activeState.sent}
                                            className="flex-1 px-2 py-1 text-sm border border-paper-border rounded focus:outline-none focus:ring-1 focus:ring-ink/30"
                                        />
                                    </div>
                                ) : (
                                    <MetaRow label="Subject" value={activeState.preview.subject} locked />
                                )}
                                {activeState.preview.hasAttachment && (
                                    <div className="flex items-center gap-1.5 text-xs text-ink-subtle pt-1">
                                        <Paperclip className="h-3 w-3" />
                                        <span>PDF attachment included</span>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">
                                    Recipients
                                </label>
                                <Textarea
                                    value={activeState.recipientsInput}
                                    onChange={(e) => handleRecipientsChange(active.id, e.target.value)}
                                    placeholder="email@example.com, another@example.com"
                                    rows={2}
                                    disabled={sendMutation.isPending || activeState.sent}
                                />
                                <div className="text-xs text-ink-subtle mt-1">
                                    Comma- or space-separated. Recipients are BCC'd — they won't see each other.
                                    {getRecipients(active.id).length > 0 && !activeState.recipientsError && (
                                        <span className="ml-1">
                                            Sending to <strong className="text-ink">{getRecipients(active.id).length}</strong> recipient{getRecipients(active.id).length !== 1 ? 's' : ''}.
                                        </span>
                                    )}
                                </div>
                                {activeState.recipientsError && (
                                    <div className="text-xs text-red-700 mt-1">{activeState.recipientsError}</div>
                                )}
                            </div>

                            <div>
                                <div className="flex items-center gap-2 mb-1.5">
                                    <Mail className="h-3.5 w-3.5 text-ink-subtle" />
                                    <span className="text-sm font-medium text-ink">Preview</span>
                                    <span className="text-xs text-ink-subtle italic">
                                        (locked — to change wording, edit the booking and resend)
                                    </span>
                                </div>
                                <div className="rounded-lg border border-paper-border overflow-hidden bg-white">
                                    <iframe
                                        title="Email preview"
                                        srcDoc={activeState.preview.html}
                                        sandbox=""
                                        className="w-full"
                                        style={{ height: '480px', border: 'none' }}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={sendMutation.isPending}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={() => sendMutation.mutate()}
                    disabled={!allReady || sendMutation.isPending}
                    loading={sendMutation.isPending}
                >
                    <Send className="h-4 w-4" />
                    {isMulti ? `Send all (${emails.length})` : 'Send'}
                </Button>
            </div>
        </>
    );
}

function MetaRow({ label, value, mono, locked }) {
    return (
        <div className="flex items-baseline gap-2 text-sm">
            <span className="text-xs uppercase tracking-wide text-ink-subtle font-medium w-16 flex-shrink-0">
                {label}
            </span>
            <span className={`text-ink ${mono ? 'font-mono text-xs' : ''} break-all flex-1`}>
                {value}
            </span>
            {locked && (
                <span className="text-[10px] uppercase tracking-wide text-ink-subtle italic flex-shrink-0">
                    locked
                </span>
            )}
        </div>
    );
}
